// Pure helpers for voice messages: which container the browser records,
// the hold / tap-to-lock gesture, the 64-sample waveform measured while
// recording, and playback (scrubbing, speeds, times).
import type { Message } from "./chat";
import type { Media } from "./media";

/** Samples in a voice message's waveform (the server takes exactly this many). */
export const WAVEFORM_SAMPLES = 64;
/** Loudness is sampled this often while recording. */
export const LEVEL_INTERVAL_MS = 50;
/** Bars in the live waveform while recording (the newest at the right). */
export const LIVE_BARS = 48;
/** Server limits when `/capabilities` predates voice clips. */
export const VOICE_MAX_MS = 5 * 60_000;
export const VOICE_MAX_BYTES = 10 * 1024 * 1024;
/** Opus speech at this rate keeps five minutes near 1.2 MB. */
export const VOICE_BITS_PER_SECOND = 32_000;
/** A press released sooner than this was a tap: it locks the recording on. */
export const TAP_MS = 350;
/** A held recording shorter than this isn't sent (a slip, not a message). */
export const MIN_VOICE_MS = 700;
/** Sliding this far left while holding cancels; this far up locks. */
export const CANCEL_DRAG_PX = 80;
export const LOCK_DRAG_PX = 60;
export const PLAYBACK_RATES = [1, 1.5, 2] as const;
export type PlaybackRate = (typeof PLAYBACK_RATES)[number];

export type VoiceClip = NonNullable<Message["voice"]>;

// ------------------------------------------------------------- recording

/**
 * What `MediaRecorder` should record, best first: Opus in WebM or Ogg, then
 * AAC in MP4 (Safari). The server takes MP4 only with AAC, so an MP4 that
 * names another codec (Opus) is never asked for.
 */
export const RECORDER_TYPES = [
  "audio/webm;codecs=opus",
  "audio/ogg;codecs=opus",
  "audio/mp4;codecs=mp4a.40.2",
  "audio/mp4",
] as const;

/** The first recorder type the browser supports, or null when it can't record a voice clip. */
export function recorderType(isTypeSupported: (type: string) => boolean): string | null {
  for (const type of RECORDER_TYPES) {
    try {
      if (isTypeSupported(type)) return type;
    } catch {
      // Some browsers throw on types they don't know.
    }
  }
  return null;
}

/**
 * The upload's content type for what the recorder said it made (`mimeType`
 * may carry codecs, or be empty, then the requested type counts); null for a
 * container the server refuses.
 */
export function clipContentType(recorded: string, requested: string): string | null {
  const base = (recorded || requested).split(";")[0]!.trim().toLowerCase();
  if (base === "audio/webm" || base === "video/webm") return "audio/webm";
  if (base === "audio/ogg") return "audio/ogg";
  if (base === "audio/mp4" || base === "video/mp4" || base === "audio/x-m4a") return "audio/mp4";
  return null;
}

/** Root-mean-square loudness 0–1 of an analyser's 8-bit time-domain samples (128 = silence). */
export function rmsLevel(samples: ArrayLike<number>): number {
  if (samples.length === 0) return 0;
  let sum = 0;
  for (let i = 0; i < samples.length; i++) {
    const v = ((samples[i] ?? 128) - 128) / 128;
    sum += v * v;
  }
  return Math.min(1, Math.sqrt(sum / samples.length));
}

/** Speech RMS rarely passes this; louder counts as full scale. */
const FULL_SCALE = 0.5;
/** Below this the clip is treated as silence rather than stretched to full height. */
const QUIET_PEAK = 0.02;

/** How tall a level draws, 0–1: square-root scaled so quiet speech still shows. */
export function levelHeight(level: number): number {
  return Math.sqrt(Math.min(1, Math.max(0, level) / FULL_SCALE));
}

/**
 * The message's waveform: `levels` (RMS 0–1, one per sampling interval)
 * over 64 equal slices, each the loudest level in it (the nearest when a
 * short clip has fewer levels than slices), scaled so the loudest slice
 * reaches 255 unless the whole clip is near silence.
 */
export function waveformOf(levels: readonly number[], samples = WAVEFORM_SAMPLES): number[] {
  if (levels.length === 0) return new Array<number>(samples).fill(0);
  const slices: number[] = [];
  for (let i = 0; i < samples; i++) {
    const from = Math.floor((i * levels.length) / samples);
    const to = Math.max(from + 1, Math.floor(((i + 1) * levels.length) / samples));
    let peak = 0;
    for (let j = from; j < to && j < levels.length; j++) peak = Math.max(peak, levels[j] ?? 0);
    slices.push(peak);
  }
  const loudest = Math.max(...slices);
  const scale = loudest < QUIET_PEAK ? 1 / FULL_SCALE : 1 / Math.min(loudest, FULL_SCALE);
  return slices.map((level) => Math.round(255 * Math.sqrt(Math.min(1, level * scale))));
}

/** The newest `count` levels for the live waveform, padded with silence at the left. */
export function liveBars(levels: readonly number[], count = LIVE_BARS): number[] {
  const tail = levels.slice(-count).map(levelHeight);
  return [...new Array<number>(count - tail.length).fill(0), ...tail];
}

/** What releasing the held mic button does. */
export type ReleaseOutcome = "lock" | "send" | "too_short";

/**
 * A press shorter than a tap locks recording on (so does one released
 * before the microphone started, as while the browser asked for it);
 * otherwise the clip goes, unless it is too short to mean anything.
 */
export function releaseOutcome(heldMs: number, recordedMs: number | null): ReleaseOutcome {
  if (heldMs < TAP_MS || recordedMs === null) return "lock";
  return recordedMs < MIN_VOICE_MS ? "too_short" : "send";
}

/** Sliding the held pointer: far enough left cancels, far enough up locks; left wins. */
export function dragOutcome(dx: number, dy: number): "cancel" | "lock" | null {
  if (dx <= -CANCEL_DRAG_PX) return "cancel";
  if (dy <= -LOCK_DRAG_PX) return "lock";
  return null;
}

/** The clip length to send: the measured time, within the server's 1 ms – limit. */
export function clipDuration(elapsedMs: number, maxMs = VOICE_MAX_MS): number {
  return Math.min(maxMs, Math.max(1, Math.round(elapsedMs)));
}

// --------------------------------------------------------------- playback

/** A running clock "m:ss" (seconds rounded down), as while recording or playing. */
export function clockText(ms: number): string {
  const seconds = Math.max(0, Math.floor(ms / 1000));
  return `${Math.floor(seconds / 60)}:${String(seconds % 60).padStart(2, "0")}`;
}

/** A clip's length "m:ss", rounded and at least 0:01, as the server's push preview says it. */
export function voiceLength(ms: number): string {
  return clockText(Math.max(1, Math.round(ms / 1000)) * 1000);
}

/** "Voice message (0:12)"; plain "Voice message" once its clip is gone. */
export function voiceText(message: Pick<Message, "voice">): string {
  return message.voice ? `Voice message (${voiceLength(message.voice.duration_ms)})` : "Voice message";
}

/** The speed after `rate`: 1× → 1.5× → 2× → 1×. */
export function nextRate(rate: number): PlaybackRate {
  const at = PLAYBACK_RATES.indexOf(rate as PlaybackRate);
  return PLAYBACK_RATES[(at + 1) % PLAYBACK_RATES.length]!;
}

export function rateText(rate: number): string {
  return `${rate}×`;
}

/** Where a pointer at `x` over a waveform spanning `left`…`left + width` seeks to, 0–1. */
export function seekFraction(x: number, left: number, width: number): number {
  if (!(width > 0)) return 0;
  return Math.min(1, Math.max(0, (x - left) / width));
}

/** Arrow keys step 5 s, Page keys 15 s, Home/End jump; null for other keys. */
export function keySeek(key: string, positionMs: number, durationMs: number): number | null {
  const step = { ArrowLeft: -5000, ArrowDown: -5000, ArrowRight: 5000, ArrowUp: 5000, PageDown: -15000, PageUp: 15000 }[
    key
  ];
  let target: number;
  if (step !== undefined) target = positionMs + step;
  else if (key === "Home") target = 0;
  else if (key === "End") target = durationMs;
  else return null;
  return Math.min(durationMs, Math.max(0, target));
}

/** Bars (of `count`) drawn as played at `fraction` of the clip. */
export function playedBars(fraction: number, count = WAVEFORM_SAMPLES): number {
  return Math.round(Math.min(1, Math.max(0, fraction)) * count);
}

/**
 * The waveform to draw: the message's 64 samples as heights 0–1 (a sliver
 * at least, so silence still shows a line), or a flat line without one.
 */
export function waveformHeights(waveform: readonly number[] | null | undefined, count = WAVEFORM_SAMPLES): number[] {
  const MIN = 0.08;
  if (!waveform || waveform.length === 0) return new Array<number>(count).fill(MIN);
  return Array.from({ length: count }, (_, i) => {
    const v = waveform[Math.floor((i * waveform.length) / count)] ?? 0;
    return Math.max(MIN, Math.min(255, v) / 255);
  });
}

/**
 * Whether this browser can play the clip, from `canPlayType` on its stored
 * type with the codec it must hold (voice clips are Opus or AAC only).
 */
export function canPlayClip(mime: string, canPlayType: (type: string) => string): boolean {
  const base = mime.split(";")[0]!.trim().toLowerCase();
  const codec = base === "audio/mp4" ? "mp4a.40.2" : "opus";
  try {
    return canPlayType(`${base};codecs=${codec}`) !== "" || canPlayType(`${base}; codecs="${codec}"`) !== "";
  } catch {
    return false;
  }
}

/** A voice message's clip (its one audio attachment), if it still has one. */
export function voiceAttachment(message: Pick<Message, "attachments">): Media | null {
  return message.attachments?.find((a) => a.kind === "audio") ?? null;
}

/** Why the microphone couldn't be used, from `getUserMedia`'s or `MediaRecorder`'s error. */
export function microphoneErrorText(error: unknown): string {
  const name = error instanceof Error ? error.name : "";
  switch (name) {
    case "NotAllowedError":
    case "SecurityError":
      return "Allow the microphone for this site to record voice messages";
    case "NotFoundError":
    case "OverconstrainedError":
      return "No microphone was found";
    case "NotReadableError":
    case "AbortError":
      return "The microphone is in use by another app";
    default:
      return "Recording didn't work";
  }
}
